import { Check, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/NewButton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type FilterSelectOption = {
  value: string;
  label: string;
  searchValue?: string;
};

function normalizeOptions(options: Array<string | FilterSelectOption>): FilterSelectOption[] {
  return options.map((option) => (
    typeof option === 'string'
      ? { value: option, label: option, searchValue: option }
      : {
          value: option.value,
          label: option.label,
          searchValue: option.searchValue ?? `${option.label} ${option.value}`,
        }
  ));
}

export function FilterSelect({
  label,
  allLabel,
  selectedNoun,
  options,
  selected,
  onChange,
}: {
  label: string;
  allLabel: string;
  selectedNoun: string;
  options: Array<string | FilterSelectOption>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedCount = selected.length;
  const buttonLabel = selectedCount === 0
    ? allLabel
    : selectedCount === 1
      ? (normalizedOptions.find((option) => option.value === selected[0])?.label ?? selected[0])
      : `${selectedCount} ${selectedNoun}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="md"
          role="combobox"
          aria-expanded={open}
          aria-label={`Filter by ${label.toLowerCase()}`}
          className={cn(
            'h-10 min-w-50 justify-between',
            selectedCount > 0 && 'border-crimson-300 bg-crimson-50/30 hover:bg-crimson-50/50',
          )}
        >
          <span className="min-w-0 truncate">{buttonLabel}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-100 p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${selectedNoun}...`} />
          <CommandList>
            <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {normalizedOptions.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.searchValue}
                    onSelect={() => {
                      onChange(
                        isSelected
                          ? selected.filter((value) => value !== option.value)
                          : [...selected, option.value],
                      );
                    }}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-xs border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <Check className="h-4 w-4" />
                    </div>
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedCount > 0 ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onChange([])}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
